import { FormComponentProps } from '@ant-design/compatible/lib/form';
import {
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Typography,
  Alert,
  Modal,
  Button,
} from 'antd';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';

import React from 'react';
import { getClient } from '../../apollo';
import {
  useVehicleTypesQuery,
  useGetVehicleBrandModelsQuery,
  useUpdateVehiclePriceConditionMutation,
  useCreateVehiclePriceConditionMutation,
  VehicleTypesQuery,
  GetVehicleBrandModelsQuery,
  ServiceSubtype,
} from '../../graphql/datamodel.gen';
import { nullIfUndefined } from '../../utils/form';

interface VehiclePriceFormContainerProps extends IVehiclePriceModal {
  serviceSubtypeId: string;
  clientId?: string;
  rateId: string;
}

const VehiclePriceFormContainer: React.FC<VehiclePriceFormContainerProps> = (
  props: VehiclePriceFormContainerProps,
) => {
  const vehicleTypesQuery = useVehicleTypesQuery({
    client: getClient(),
  });

  const vehicleBrandModelsQuery = useGetVehicleBrandModelsQuery({
    client: getClient(),
  });

  const vehicleTypes = vehicleTypesQuery.data?.vehicleTypes || [];
  const vehicleBrandModels =
    vehicleBrandModelsQuery.data?.vehicleBrandModels || [];

  const [updateVehiclePriceConditionMut] =
    useUpdateVehiclePriceConditionMutation({
      client: getClient(),
    });

  const [createVehiclePriceConditionMut] =
    useCreateVehiclePriceConditionMutation({
      client: getClient(),
    });

  const updateVehiclePriceCondition = async (
    data: IVehiclePriceFormValues & {
      id?: string;
      vehicleModelId?: string;
      vehicleBrandId?: string;
      vehicleTypeId?: string;
    },
  ) => {
    if (data.id) {
      await updateVehiclePriceConditionMut({
        variables: {
          description: data.description,
          id: data.id,
          name: data.name,
          numPassengers: data.numPassengers,
          price: data.price,
          extraHourPrice: data.extraHourPrice,
          vehicleBrandId: data.vehicleBrandId,
          vehicleModelId: data.vehicleModelId,
          vehicleTypeId: data.vehicleTypeId,
        },
      });
      props.onClose();
    } else {
      await createVehiclePriceConditionMut({
        variables: {
          rateId: props.rateId,
          serviceSubtypeId: props.serviceSubtypeId,
          description: data.description,
          name: data.name,
          numPassengers: data.numPassengers,
          price: data.price,
          extraHourPrice: data.extraHourPrice,
          vehicleBrandId: data.vehicleBrandId,
          vehicleModelId: data.vehicleModelId,
          vehicleTypeId: data.vehicleTypeId,
        },
      });
      props.onClose();
    }

    // update
  };

  return (
    <MyVehiclePriceForm
      vehiclePriceFilter={props.vehiclePriceFilter}
      onSubmit={updateVehiclePriceCondition}
      vehicleTypes={vehicleTypes}
      vehicleBrandModels={vehicleBrandModels}
    />
  );
};

interface IVehiclePriceForm extends FormComponentProps {
  vehicleTypes: VehicleTypesQuery['vehicleTypes'];
  vehicleBrandModels: GetVehicleBrandModelsQuery['vehicleBrandModels'];
  onSubmit: (
    data: IVehiclePriceFormValues & {
      id?: string;
      vehicleModelId?: string;
      vehicleBrandId?: string;
      vehicleTypeId?: string;
    },
  ) => any;
  vehiclePriceFilter?: ServiceSubtype['priceConditions']['vehicle'][number];
}

interface IVehiclePriceFormState {
  selectedVehicleBrandId?: string;
  selectedVehicleModelId?: string;
  selectedVehicleTypeId?: string;
  validationError?: string;
}

interface IVehiclePriceFormValues {
  numPassengers: number;
  price: number;
  extraHourPrice?: number;
  description: string;
  name: string;
  vehicleBrandId?: string;
  vehicleModelId?: string;
  vehicleTypeId: string;
}

class VehiclePriceForm extends React.Component<
  IVehiclePriceForm,
  IVehiclePriceFormState
> {
  state = {
    selectedVehicleBrandId: undefined,
    selectedVehicleModelId: undefined,
    selectedVehicleTypeId: undefined,
    validationError: undefined,
  };

  constructor(props: Readonly<IVehiclePriceForm>) {
    super(props);

    this.state = {
      selectedVehicleBrandId: this.props.vehiclePriceFilter?.vehicleBrandId,
      selectedVehicleModelId: this.props.vehiclePriceFilter?.vehicleModelId,
      selectedVehicleTypeId: this.props.vehiclePriceFilter?.vehicleTypeId,
      validationError: undefined,
    };
  }

  static getDerivedStateFromProps(
    nextProps: IVehiclePriceForm,
    previousState: IVehiclePriceFormState,
  ): IVehiclePriceFormState | null {
    return {
      ...previousState,
      selectedVehicleBrandId: nextProps.vehiclePriceFilter?.vehicleBrandId,
      selectedVehicleModelId: nextProps.vehiclePriceFilter?.vehicleModelId,
      selectedVehicleTypeId: nextProps.vehiclePriceFilter?.vehicleTypeId,
    };
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields((err, values: IVehiclePriceFormValues) => {
      if (!err) {
        if (values.price === 0) {
          return this.setState({
            validationError: 'El precio no puede ser cero',
          });
        }
        if (
          !this.state.selectedVehicleModelId &&
          !this.state.selectedVehicleModelId &&
          !this.state.selectedVehicleTypeId
        ) {
          this.setState({
            validationError:
              'Debes seleccionar almenos un tipo y/o marca y/o modelo de vehículo',
          });
        }
        this.setState({
          validationError: undefined,
        });

        this.props.onSubmit({
          name: values.name,
          id: this.props.vehiclePriceFilter?.id,
          description: values.description,
          numPassengers: values.numPassengers,
          price: values.price,
          extraHourPrice: values.extraHourPrice,
          vehicleModelId: nullIfUndefined(values.vehicleModelId),
          vehicleTypeId: nullIfUndefined(values.vehicleTypeId),
          vehicleBrandId: nullIfUndefined(values.vehicleBrandId),
        });
      }
    });
  };

  render() {
    const allModels = [];

    const { getFieldDecorator } = this.props.form;

    this.props.vehicleBrandModels.map((brand) => {
      allModels.push(...(brand.models || []));
    });

    const models: GetVehicleBrandModelsQuery['vehicleBrandModels'][number]['models'] =
      this.props.vehicleBrandModels.find(
        (brand) => brand.id === this.state.selectedVehicleBrandId,
      )?.models || allModels;

    return (
      <Form
        id={'vehicle-price-form'}
        onSubmit={this.handleSubmit}
        layout="vertical"
      >
        <Form.Item label={'Nombre'} required>
          {this.props.form.getFieldDecorator('name', {
            initialValue: this.props.vehiclePriceFilter?.name,
            rules: [
              {
                required: true,
                message: 'Por favor, introduce un nombre',
              },
            ],
          })(<Input />)}
        </Form.Item>
        <Form.Item label={'Descripción'}>
          {this.props.form.getFieldDecorator('description', {
            initialValue: this.props.vehiclePriceFilter?.description,
          })(<Input />)}
        </Form.Item>
        <Form.Item label={'Tipo'} required>
          {getFieldDecorator('vehicleTypeId', {
            initialValue: this.props.vehiclePriceFilter?.vehicleTypeId,
            rules: [
              {
                required: true,
                message: 'Por favor, selecciona un tipo de vehículos',
              },
            ],
          })(
            <Select
              placeholder={this.props.vehicleTypes
                .map((type) => type.name.toUpperCase())
                .join(', ')}
              allowClear={true}
            >
              {this.props.vehicleTypes.map((type) => {
                return (
                  <Select.Option key={type.id} value={type.id}>
                    {type.name.toUpperCase()}
                  </Select.Option>
                );
              })}
            </Select>,
          )}
        </Form.Item>
        <Row>
          <Col span={12}>
            <Form.Item label={'Marca'} style={{ flex: 1, marginRight: '15px' }}>
              {getFieldDecorator('vehicleBrandId', {
                initialValue: this.props.vehiclePriceFilter?.vehicleBrandId,
              })(
                <Select allowClear={true} placeholder={'Selecciona una marca'}>
                  {this.props.vehicleBrandModels.map((brandModels) => {
                    return (
                      <Select.Option
                        key={brandModels.id}
                        value={brandModels.id}
                      >
                        {brandModels.brand.toUpperCase()}
                      </Select.Option>
                    );
                  })}
                </Select>,
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={'Modelo'} style={{ flex: 1 }}>
              {getFieldDecorator('vehicleModelId', {
                initialValue: this.props.vehiclePriceFilter?.vehicleModelId,
              })(
                <Select allowClear={true} placeholder={'Selecciona un modelo'}>
                  {models.map((model) => {
                    return (
                      <Select.Option key={model.id} value={model.id}>
                        {model.name.toUpperCase()}
                      </Select.Option>
                    );
                  })}
                </Select>,
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Form.Item label={'Capacidad (# Pasajeros)'} required>
              {this.props.form.getFieldDecorator('numPassengers', {
                initialValue: this.props.vehiclePriceFilter?.numPassengers || 0,
                rules: [
                  {
                    required: true,
                    message: 'Por favor, introduce la capacidad (# pasajeros)',
                  },
                ],
              })(<InputNumber min={0} />)}{' '}
            </Form.Item>
          </Col>
          <Col span={12}></Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item label={'Precio base'} required>
              {this.props.form.getFieldDecorator('price', {
                initialValue: this.props.vehiclePriceFilter?.basePrice || 0,
                rules: [
                  {
                    required: true,
                    message: 'Por favor, introduce un precio',
                  },
                ],
              })(
                <InputNumber decimalSeparator={','} min={0} step={0.1} />,
              )}{' '}
              <Typography.Text strong={true}> €</Typography.Text>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={'Hora extra'}>
              {this.props.form.getFieldDecorator('extraHourPrice', {
                initialValue:
                  this.props.vehiclePriceFilter?.extraHourPrice || 0,
                rules: [
                  {
                    required: false,
                    message: 'Por favor, introduce un precio',
                  },
                ],
              })(
                <InputNumber decimalSeparator={','} min={0} step={0.1} />,
              )}{' '}
              <Typography.Text strong={true}> €</Typography.Text>
            </Form.Item>
          </Col>
        </Row>

        {this.state.validationError && (
          <Alert
            description={this.state.validationError}
            message={'Error de validación'}
            type="error"
          />
        )}
      </Form>
    );
  }
}

const MyVehiclePriceForm = Form.create<IVehiclePriceForm>()(VehiclePriceForm);

interface IVehiclePriceModal {
  visible: boolean;
  onClose: () => any;
  vehiclePriceFilter?: ServiceSubtype['priceConditions']['vehicle'][number];
  serviceSubtypeId: string;
  clientId?: string;
  rateId: string;
}

export const VehiclePriceModal: React.FC<IVehiclePriceModal> = (
  props: IVehiclePriceModal,
) => {
  return (
    <Modal
      footer={[
        <Button danger onClick={props.onClose} style={{ float: 'left' }}>
          Cerrar sin guardar
        </Button>,
        <Button
          htmlType={'submit'}
          form={'vehicle-price-form'}
          type={'primary'}
          icon={<LegacyIcon type={'plus'} />}
        >
          {props.vehiclePriceFilter ? 'Guardar' : 'Añadir'}
        </Button>,
      ]}
      width={400}
      title={
        props.vehiclePriceFilter
          ? 'Gestionar precio de vehículo'
          : 'Añadir nuevo precio de vehículo'
      }
      onCancel={props.onClose}
      visible={props.visible}
      centered={true}
      maskClosable={true}
      destroyOnClose={true}
      forceRender={true}
    >
      <VehiclePriceFormContainer
        {...props}
        vehiclePriceFilter={props.vehiclePriceFilter}
      />
    </Modal>
  );
};
